// ============================================================================
// VoiceRoomEditDialog — переименование/тема существующей комнаты.
//
// Права проверяет СЕРВЕР (UpdateRoom: создатель, модератор комнаты, админ);
// клиент показывает действие только владельцу/админу. Поля — presence:
// отправляем ТОЛЬКО name/topic, настройки доступа (settings) не трогаем.
// ============================================================================

import { useEffect, useState } from 'react';
import type { VoiceRoom } from '@zdc/sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { getSDK } from '@/store/sdkStore';
import { useToastStore } from '@/store/toastStore';

export interface VoiceRoomEditDialogProps {
  open: boolean;
  room: VoiceRoom | null;
  onClose: () => void;
  onUpdated: (room: VoiceRoom) => void;
}

export function VoiceRoomEditDialog({ open, room, onClose, onUpdated }: VoiceRoomEditDialogProps) {
  const [name, setName] = useState('');
  const [topic, setTopic] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !room) return;
    setName(room.name);
    setTopic(room.topic);
    setError('');
  }, [open, room]);

  if (!room) return null;

  const submit = async () => {
    const nextName = name.trim();
    if (!nextName) {
      setError('Укажите имя комнаты');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const updated = await getSDK().voiceRooms.updateRoom({
        roomId: room.id,
        name: nextName,
        topic: topic.trim(),
      });
      useToastStore.getState().showSuccess('Комната обновлена');
      onUpdated(updated);
      onClose();
    } catch (e: unknown) {
      logErrorToJournal(e, 'Голосовые комнаты: переименование комнаты');
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <UniversalWindow
      open={open}
      onClose={onClose}
      title="Свойства комнаты"
      width="w-[min(480px,94vw)]"
      height="auto"
      resizable={false}
    >
      <div className="space-y-3 p-3">
        <div className="space-y-1">
          <label className="text-xs font-medium" htmlFor="voice-room-name">Имя комнаты *</label>
          <Input
            id="voice-room-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
            placeholder="Например, Переговорная"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void submit();
              }
            }}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium" htmlFor="voice-room-topic">Тема</label>
          <Input
            id="voice-room-topic"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            disabled={busy}
            placeholder="О чём комната (необязательно)"
          />
        </div>
        {error && <div className="text-xs text-destructive">{error}</div>}
        <div className="flex items-center justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button size="sm" onClick={() => void submit()} disabled={busy || !name.trim()}>
            {busy ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        </div>
      </div>
    </UniversalWindow>
  );
}
